import React from "react";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import { BlockQuoteItem } from "@insightfulscience/atomic-react/r1/molecules/BlockQuote";
import Section from "@insightfulscience/atomic-react/Section";
import ContentContainer from "@insightfulscience/atomic-react/r1/atoms/ContentContainer";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Heading from "@insightfulscience/atomic-react/Heading";
import Button from "@insightfulscience/atomic-react/Button";
import Image from "@insightfulscience/atomic-react/Image";
import Link from "@insightfulscience/atomic-react/Link";
import NextLink from "@insightfulscience/smart-react/NextLink";
import FeaturesBlock from "@insightfulscience/atomic-react/r1/molecules/FeaturesBlock";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import UList from "@insightfulscience/atomic-react/UList";
import Html from "@insightfulscience/smart-react/Html";
import SmartTabs from "@insightfulscience/atomic-react/utils/SmartTabs";
import Tabs from "@insightfulscience/atomic-react/Tabs";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import Inline from "@insightfulscience/atomic-react/Inline";
import Block from "@insightfulscience/atomic-react/Block";
import SectionTryForFree from "@src/SectionTryForFree";
import SiteLayout from "@src/SiteLayout";
import PreloadImages from "@src/PreloadImages";
import resources from "../locales/features-page";

const Features = () => {
	const [t] = useLocale("features");
	return (
		<SiteLayout isBorderBottomNone>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					isImageBaseUrl
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
				<PreloadImages
					imagesHref={[
						"/images/snapgene/features/features-visualize-large.svg",
						"/images/snapgene/features/features-plan.svg",
						"/images/snapgene/features/features-visualize.svg",
						"/images/snapgene/features/features-document.svg",
						"/images/snapgene/features/1_Improve_Accuracy.png",
					]}
				/>
			</PageMeta>
			<Section spacing={{ py: 8 }}>
				<ContentContainer lg={11} center>
					<Row middle>
						<Col lg="auto" className="small-order-2 large-order-1">
							<Heading
								Tag="h1"
								sx={{ fontSize: "xxl", textAlign: "center" }}
								className="large-text-left"
							>
								{t("HERO_TITLE")}
							</Heading>
							<Row spacing={{ mt: 8 }}>
								<Col sx={{ textAlign: "center" }} className="large-text-left">
									<Button.Link href="/free-trial" size="s" className="mr-4">
										{t("HERO_TRY_FOR_FREE")}
									</Button.Link>
									<Button.Link href="/pricing" size="s" styleType="secondary" isOutline2>
										{t("HERO_TRY_FOR_PRICING")}
									</Button.Link>
								</Col>
							</Row>
						</Col>
						<Col lg="shrink" sx={{ textAlign: "center" }} className="small-order-1 large-order-2">
							<Image
								src="/images/snapgene/features/features-visualize-large.svg"
								className="w-40 w-full--lg"
							/>
						</Col>
					</Row>
				</ContentContainer>
			</Section>
			<Section
				utility={{
					border: {
						top: {
							width: 1,
							color: "black--15",
						},
						bottom: {
							width: 1,
							color: "black--15",
						},
					},
				}}
				spacing={{ py: 8 }}
			>
				<ContentContainer lg={11} center>
					<div className="features-justify-links">
						<div className="features-justify-links__item">
							<Link sx={{ fontSize: "l" }} href="#beyond-the-basics-of-molecular-cloning">
								{t("MOLECULAR_CLONING_TITLE")}
							</Link>
						</div>
						<div className="features-justify-links__item">
							<Link sx={{ fontSize: "l" }} href="#verify-your-project-design-and-prevent-errors">
								{t("ALIGN_TITLE")}
							</Link>
						</div>
						<div className="features-justify-links__item">
							<Link
								sx={{ fontSize: "l" }}
								href="#configured-for-automatic-documentation-and-easy-data-exchange"
							>
								{t("DATA_TITLE")}
							</Link>
						</div>
						<div className="features-justify-links__item">
							<Link sx={{ fontSize: "l" }} href="#all-features">
								{t("ALL_FEATURES_TITLE")}
							</Link>
						</div>
					</div>
				</ContentContainer>
			</Section>
			<Section
				utility={{ border: { bottom: { width: 1, color: "black--15" } } }}
				spacing={{ py: 16 }}
			>
				<ContentContainer lg={11} center>
					<Heading Tag="h2" sx={{ fontSize: "xl", textAlign: "center" }} spacing={{ mb: 12 }}>
						{t("VISUALISE_TITLE")}
					</Heading>
					<SmartTabs initialActiveTab="features-plan">
						<Tabs className="mt-6">
							<SmartTabs.Tab id="features-plan" className="h-34" href="#" cell="medium-4">
								<Inline>
									<Inline
										utility={{ display: "block" }}
										sx={{ textAlign: "center" }}
										spacing={{ mb: 4 }}
									>
										<Image src="/images/snapgene/features/features-plan.svg" />
									</Inline>
									<Inline utility={{ display: "block" }} sx={{ textAlign: "center" }}>
										{t("PLAN_TAB_TITLE")}
									</Inline>
								</Inline>
							</SmartTabs.Tab>
							<SmartTabs.Tab id="features-visualize" className="h-34" href="#" cell="medium-4">
								<Inline>
									<Inline
										utility={{ display: "block" }}
										sx={{ textAlign: "center" }}
										spacing={{ mb: 4 }}
									>
										<Image src="/images/snapgene/features/features-visualize.svg" />
									</Inline>
									<Inline utility={{ display: "block" }} sx={{ textAlign: "center" }}>
										{t("VISUALIZE_TAB_TITLE")}
									</Inline>
								</Inline>
							</SmartTabs.Tab>
							<SmartTabs.Tab id="features-document" className="h-34" href="#" cell="medium-4">
								<Inline>
									<Inline
										utility={{ display: "block" }}
										sx={{ textAlign: "center" }}
										spacing={{ mb: 4 }}
									>
										<Image src="/images/snapgene/features/features-document.svg" />
									</Inline>
									<Inline utility={{ display: "block" }} sx={{ textAlign: "center" }}>
										{t("DOCUMENT_TAB_TITLE")}
									</Inline>
								</Inline>
							</SmartTabs.Tab>
						</Tabs>
						<Row className="tabs-content mt-10">
							<SmartTabs.TabBody.Mounted id="features-plan" Component={Col}>
								<FeaturesBlock
									className="mb-12"
									imgSrc="/images/snapgene/features/1_Improve_Accuracy.png"
									title={t("PLAN_TAB_CONTENT_TITLE")}
								>
									<FeaturesBlock.Item>{t("PLAN_TAB_CONTENT_1")}</FeaturesBlock.Item>
									<FeaturesBlock.Item>{t("PLAN_TAB_CONTENT_2")}</FeaturesBlock.Item>
									<FeaturesBlock.Item>{t("PLAN_TAB_CONTENT_3")}</FeaturesBlock.Item>
								</FeaturesBlock>
							</SmartTabs.TabBody.Mounted>
							<SmartTabs.TabBody.Mounted
								id="features-visualize"
								className="width-100"
								Component={Col}
							>
								<FeaturesBlock
									className="mb-12"
									imgSrc="/images/snapgene/features/5_agarose_gel.png"
									title={t("VISUALISE_TAB_CONTENT_TITLE")}
								>
									<FeaturesBlock.Item>{t("VISUALISE_TAB_CONTENT_1")}</FeaturesBlock.Item>
									<FeaturesBlock.Item>{t("VISUALISE_TAB_CONTENT_2")}</FeaturesBlock.Item>
									<FeaturesBlock.Item>{t("VISUALISE_TAB_CONTENT_3")}</FeaturesBlock.Item>
								</FeaturesBlock>
							</SmartTabs.TabBody.Mounted>
							<SmartTabs.TabBody.Mounted
								id="features-document"
								className="width-100"
								Component={Col}
							>
								<FeaturesBlock
									className="mb-12"
									imgSrc="/images/snapgene/features/6_Automatically_create_graphical_history.png"
									title={t("DOCUMENT_TAB_CONTENT_TITLE")}
								>
									<FeaturesBlock.Item>{t("DOCUMENT_TAB_CONTENT_1")}</FeaturesBlock.Item>
									<FeaturesBlock.Item>{t("DOCUMENT_TAB_CONTENT_2")}</FeaturesBlock.Item>
									<FeaturesBlock.Item>{t("DOCUMENT_TAB_CONTENT_3")}</FeaturesBlock.Item>
								</FeaturesBlock>
							</SmartTabs.TabBody.Mounted>
						</Row>
					</SmartTabs>
					<BlockQuoteItem
						author={t("QUOTE_AUTHOR")}
						institution={t("QUOTE_INSTITUTION")}
						active
						className="mb-12"
					>
						{t("QUOTE_CONTENT")}
					</BlockQuoteItem>
					<Row>
						<Col>
							<Block sx={{ textAlign: "center" }}>
								<Button.Link href="/free-trial" size="s">
									{t("TRY_FOR_FREE")}
								</Button.Link>
							</Block>
						</Col>
					</Row>
				</ContentContainer>
			</Section>
			<Section
				id="beyond-the-basics-of-molecular-cloning"
				utility={{ border: { bottom: { width: 1, color: "black--15" } } }}
				spacing={{ py: 16 }}
			>
				<ContentContainer lg={11} center>
					<Heading Tag="h2" sx={{ fontSize: "xl", textAlign: "center" }}>
						{t("BEYOND_SECTION_TITLE")}
					</Heading>
					<Paragraph sx={{ fontSize: "l", textAlign: "center" }} spacing={{ mb: 12 }}>
						{t("BEYOND_SECTION_SUBTITLE")}
					</Paragraph>
					<Row utility={{ justify: "center" }} spacing={{ mb: 12 }}>
						<Col lg={6}>
							<Image src="/images/snapgene/features/2_Beyond_the_Basics.png" />
						</Col>
					</Row>
					<Row utility={{ justify: "center" }} spacing={{ mb: 12 }}>
						<Col lg={8}>
							<UList>
								<UList.Item spacing={{ mb: 4 }}>{t("BEYOND_SECTION_LIST_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 4 }}>{t("BEYOND_SECTION_LIST_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 4 }}>{t("BEYOND_SECTION_LIST_ITEM_3")}</UList.Item>
							</UList>
						</Col>
					</Row>
					<Row utility={{ align: { items: "end" } }} spacing={{ mb: 0, md: { mb: 12 } }}>
						<Col md={4} sx={{ textAlign: "center" }} spacing={{ sm: { mb: 6 }, md: { mb: 0 } }}>
							<NextLink utility={{ display: "block" }} href="/series/simulate-restriction-cloning">
								<Image src="/images/snapgene/features/features-cloning.png" className="w-60" />
								<Inline sx={{ fontSize: "l", fontWeight: "medium" }} utility={{ display: "block" }}>
									{t("RESTRICTION_CLONING")}
								</Inline>
							</NextLink>
						</Col>
						<Col md={4} sx={{ textAlign: "center" }} spacing={{ sm: { mb: 6 }, md: { mb: 0 } }}>
							<NextLink utility={{ display: "block" }} href="/series/simulate-gateway-cloning">
								<Image src="/images/snapgene/features/features-cloning-4.png" className="w-60" />
								<Inline sx={{ fontSize: "l", fontWeight: "medium" }} utility={{ display: "block" }}>
									{t("GATEWAY_CLONING")}
								</Inline>
							</NextLink>
						</Col>
						<Col md={4} sx={{ textAlign: "center" }} spacing={{ sm: { mb: 6 }, md: { mb: 0 } }}>
							<NextLink utility={{ display: "block" }} href="/series/simulate-gibson-assembly">
								<Image src="/images/snapgene/features/features-cloning-1.png" className="w-60" />
								<Inline sx={{ fontSize: "l", fontWeight: "medium" }} utility={{ display: "block" }}>
									{t("GIBSON_ASSEMBLY")}
								</Inline>
							</NextLink>
						</Col>
					</Row>
					<Row utility={{ align: { items: "end" } }} spacing={{ mb: 0, md: { mb: 12 } }}>
						<Col md={4} sx={{ textAlign: "center" }} spacing={{ sm: { mb: 6 }, md: { mb: 0 } }}>
							<NextLink utility={{ display: "block" }} href="/series/simulate-in-fusion-cloning">
								<Image src="/images/snapgene/features/features-cloning-5.png" className="w-60" />
								<Inline sx={{ fontSize: "l", fontWeight: "medium" }} utility={{ display: "block" }}>
									{t("IN_FUSION_CLONING")}
								</Inline>
							</NextLink>
						</Col>
						<Col md={4} sx={{ textAlign: "center" }} spacing={{ sm: { mb: 6 }, md: { mb: 0 } }}>
							<NextLink utility={{ display: "block" }} href="/series/simulate-ta-cloning">
								<Image src="/images/snapgene/features/features-cloning-2.png" className="w-60" />
								<Inline sx={{ fontSize: "l", fontWeight: "medium" }} utility={{ display: "block" }}>
									{t("TA_CLONING")}
								</Inline>
							</NextLink>
						</Col>
						<Col md={4} sx={{ textAlign: "center" }}>
							<NextLink utility={{ display: "block" }} href="/series/simulate-topo-cloning">
								<Image src="/images/snapgene/features/features-cloning-3.png" className="w-60" />
								<Inline sx={{ fontSize: "l", fontWeight: "medium" }} utility={{ display: "block" }}>
									{t("TOPO_CLONING")}
								</Inline>
							</NextLink>
						</Col>
					</Row>
					<Row bottom spacing={{ mb: 12 }}>
						<Col md={4} sx={{ textAlign: "center" }}>
							<NextLink
								utility={{ display: "block" }}
								href="/series/simulate-nebuilder-hifi-assembly"
							>
								<Image src="/images/snapgene/features/features-cloning-6.png" className="w-60" />
								<Inline sx={{ fontSize: "l", fontWeight: "medium" }} utility={{ display: "block" }}>
									{t("NEBUILDER_HI_FI_ASSEMBLY")}
								</Inline>
							</NextLink>
						</Col>
						<Col md={4} sx={{ textAlign: "center" }}>
							<NextLink utility={{ display: "block" }} href="/series/simulate-golden-gate-assembly">
								<Image src="/images/snapgene/features/golden-gate-diagram.png" className="w-60" />
								<Inline sx={{ fontSize: "l", fontWeight: "medium" }} utility={{ display: "block" }}>
									{t("GOLDEN_GATE_ASSEMBLY")}
								</Inline>
							</NextLink>
						</Col>
					</Row>
					<BlockQuoteItem
						author={t("BEYOND_QUOTE_AUTHOR")}
						institution={t("BEYOND_QUOTE_INSTITUTION")}
						active
						className="mb-12"
					>
						{t("BEYOND_QUOTE_CONTENT")}
					</BlockQuoteItem>
					<Row>
						<Col>
							<Block sx={{ textAlign: "center" }}>
								<Button.Link href="/free-trial" size="s">
									{t("TRY_FOR_FREE")}
								</Button.Link>
							</Block>
						</Col>
					</Row>
				</ContentContainer>
			</Section>
			<Section
				id="verify-your-project-design-and-prevent-errors"
				utility={{ border: { bottom: { width: 1, color: "black--15" } } }}
				spacing={{ py: 16 }}
			>
				<ContentContainer lg={11} center>
					<Heading Tag="h2" sx={{ fontSize: "xl", textAlign: "center" }} spacing={{ mb: 12 }}>
						{t("VERIFY_SECTION_TITLE")}
					</Heading>
					<FeaturesBlock
						className="mb-12"
						imgSrc="/images/snapgene/features/3_Automatically_View_Plasmid.png"
						title={t("VERIFY_AUTOMATICALLY_TITLE")}
					>
						<FeaturesBlock.Item>{t("VERIFY_AUTOMATICALLY_CONTENT_1")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("VERIFY_AUTOMATICALLY_CONTENT_2")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("VERIFY_AUTOMATICALLY_CONTENT_3")}</FeaturesBlock.Item>
					</FeaturesBlock>
					<FeaturesBlock
						className="mb-12"
						isImgFirst={false}
						imgSrc="/images/snapgene/features/4_check_your_sequence.png"
						title={t("VERIFY_CHECK_TITLE")}
					>
						<FeaturesBlock.Item>{t("VERIFY_CHECK_CONTENT_1")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("VERIFY_CHECK_CONTENT_2")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("VERIFY_CHECK_CONTENT_3")}</FeaturesBlock.Item>
					</FeaturesBlock>
					<FeaturesBlock
						className="mb-12 align-middle"
						imgSrc="/images/snapgene/features/5_agarose_gel.png"
						imgClassName="max-h-82"
						title={t("VERIFY_REALISTIC_TITLE")}
					>
						<FeaturesBlock.Item>{t("VERIFY_REALISTIC_CONTENT_1")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("VERIFY_REALISTIC_CONTENT_2")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("VERIFY_REALISTIC_CONTENT_3")}</FeaturesBlock.Item>
					</FeaturesBlock>
					<BlockQuoteItem
						author={t("VERIFY_QUOTE_AUTHOR")}
						institution={t("VERIFY_QUOTE_INSTITUTION")}
						active
						className="my-12"
					>
						{t("VERIFY_QUOTE_CONTENT")}
					</BlockQuoteItem>
					<Row>
						<Col>
							<Block sx={{ textAlign: "center" }}>
								<Button.Link href="/free-trial" size="s">
									{t("TRY_FOR_FREE")}
								</Button.Link>
							</Block>
						</Col>
					</Row>
				</ContentContainer>
			</Section>
			<Section
				id="configured-for-automatic-documentation-and-easy-data-exchange"
				utility={{ border: { bottom: { width: 1, color: "black--15" } } }}
				spacing={{ py: 16 }}
			>
				<ContentContainer lg={11} center>
					<Heading Tag="h2" sx={{ fontSize: "xl", textAlign: "center" }} spacing={{ mb: 12 }}>
						{t("CONFIGURED_SECTION_TITLE")}
					</Heading>
					<FeaturesBlock
						className="mb-12"
						imgSrc="/images/snapgene/features/6_Automatically_create_graphical_history.png"
						title={t("CONFIGURED_AUTOMATICALLY_TITLE")}
					>
						<FeaturesBlock.Item>{t("CONFIGURED_AUTOMATICALLY_CONTENT_1")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("CONFIGURED_AUTOMATICALLY_CONTENT_2")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("CONFIGURED_AUTOMATICALLY_CONTENT_3")}</FeaturesBlock.Item>
					</FeaturesBlock>
					<FeaturesBlock
						className="mb-12"
						isImgFirst={false}
						imgSrc="/images/snapgene/features/7_Convery_and_Share_data.png"
						title={t("CONFIGURED_CONVERT_TITLE")}
					>
						<FeaturesBlock.Item>{t("CONFIGURED_CONVERT_CONTENT_1")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>{t("CONFIGURED_CONVERT_CONTENT_2")}</FeaturesBlock.Item>
						<FeaturesBlock.Item>
							<Html>
								{t("CONFIGURED_CONVERT_CONTENT_3", {
									link: "/features/convert-file-formats",
								})}
							</Html>
						</FeaturesBlock.Item>
					</FeaturesBlock>
					<BlockQuoteItem
						author={t("CONFIGURED_QUOTE_AUTHOR")}
						institution={t("CONFIGURED_QUOTE_INSTITUTION")}
						active
						className="mb-12"
					>
						{t("CONFIGURED_QUOTE_CONTENT")}
					</BlockQuoteItem>
					<Row>
						<Col>
							<Block sx={{ textAlign: "center" }}>
								<Button.Link href="/free-trial" size="s">
									{t("TRY_FOR_FREE")}
								</Button.Link>
							</Block>
						</Col>
					</Row>
				</ContentContainer>
			</Section>
			<Section id="all-features" spacing={{ py: 16 }}>
				<ContentContainer lg={11} center>
					<Heading Tag="h2" sx={{ fontSize: "xl", textAlign: "center" }}>
						{t("ALL_FEATURES_TITLE")}
					</Heading>
					<Paragraph sx={{ fontSize: "l", textAlign: "center" }} spacing={{ mb: 12 }}>
						{t("ALL_FEATURES_SUBTITLE_1")} <br />
						{t("ALL_FEATURES_SUBTITLE_2")}
					</Paragraph>
					<Row>
						<Col md={6} className="mb-md-8">
							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("MOLECULAR_CLONING_FEATURES_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("MOLECULAR_CLONING_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("MOLECULAR_CLONING_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("MOLECULAR_CLONING_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("MOLECULAR_CLONING_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("MOLECULAR_CLONING_ITEM_5")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("MOLECULAR_CLONING_ITEM_6")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("MOLECULAR_CLONING_ITEM_7")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("PRIMERS_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("PRIMERS_CLONING_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("PRIMERS_CLONING_ITEM_2")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("PCR_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("PCR_CLONING_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("PCR_CLONING_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("PCR_CLONING_ITEM_3")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("ENZYME_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("ENZYME_CLONING_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("ENZYME_CLONING_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("ENZYME_CLONING_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("ENZYME_CLONING_ITEM_4")}</UList.Item>
							</UList>
							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("CONVERT_FILE_FORMATS")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_5")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_6")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_7")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_8")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_9")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_10")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_11")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_12")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_13")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_14")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_15")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_16")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_17")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_18")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_19")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_20")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_21")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_22")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_23")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("CONVERT_FILE_FORMATS_ITEM_24")}</UList.Item>
							</UList>
						</Col>
						<Col md={6}>
							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("AGAROSE_TILE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("AGAROSE_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("AGAROSE_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("AGAROSE_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("AGAROSE_ITEM_4")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("FEATURES_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("FEATURES_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("FEATURES_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("FEATURES_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("FEATURES_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("FEATURES_ITEM_5")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("FEATURES_ITEM_6")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("TRANSLATIONS_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("TRANSLATIONS_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("TRANSLATIONS_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("TRANSLATIONS_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("TRANSLATIONS_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("TRANSLATIONS_ITEM_5")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("TRANSLATIONS_ITEM_6")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("ALIGNMENT_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("ALIGNMENT_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("ALIGNMENT_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("ALIGNMENT_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("ALIGNMENT_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("ALIGNMENT_ITEM_5")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("ALIGNMENT_ITEM_6")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("VISUALIZING_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("VISUALIZING_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("VISUALIZING_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("VISUALIZING_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("VISUALIZING_ITEM_4")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("HISTORY_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("HISTORY_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("HISTORY_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("HISTORY_ITEM_3")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("DATA_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>
									<Html>
										{t("DATA_ITEM_1", {
											importLink: "/features/convert-file-formats",
										})}
									</Html>
								</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("DATA_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("DATA_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("DATA_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("DATA_ITEM_5")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("SEARCH_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("SEARCH_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2 }}>{t("SEARCH_ITEM_2")}</UList.Item>
							</UList>

							<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 4 }}>
								{t("GENERAL_TITLE")}
							</Heading>
							<UList spacing={{ mb: 8 }}>
								<UList.Item spacing={{ mb: 2 }}>{t("GENERAL_ITEM_1")}</UList.Item>
							</UList>
						</Col>
					</Row>
				</ContentContainer>
			</Section>
			<SectionTryForFree />
		</SiteLayout>
	);
};

export default pipe(Features, WithLocale({ resources }));
